import {
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import InputLabel from 'ui-component/extended/Form/InputLabel';

import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { PromoSelect } from 'components/select/promoCodeSelect';
import { StaticSelect } from 'components/select/staticSelect';
import { optionPromoDuration } from 'constants/promoCodeManagement';
import MainCard from 'ui-component/cards/MainCard';

export const BrandedAppPromoCodeForm = (props: any) => {
    const { values, handleBlur, handleChange, touched, errors, setFieldValue, isSubmitting, setValues, paramsId } = props;

    return (
        <MainCard title={paramsId ? 'Edit Branded App Promo Code' : 'Add Branded App Promo Code'}>
            <Grid container spacing={3}>
                {/* Title */}
                <Grid item xs={12} sm={6}>
                    <InputLabel>Title</InputLabel>
                    <TextField
                        fullWidth
                        placeholder="Title"
                        className="FormInputLabel"
                        value={values.title}
                        name="title"
                        onBlur={handleBlur}
                        onChange={handleChange}
                    />
                    {touched.title && errors.title && (
                        <FormHelperText error id="title-error">
                            {errors.title}
                        </FormHelperText>
                    )}
                </Grid>

                {/* Code */}
                <Grid item xs={12} sm={6}>
                    <InputLabel>Code</InputLabel>
                    <TextField
                        fullWidth
                        placeholder="Promo code"
                        className="FormInputLabel"
                        value={values.code}
                        name="code"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        disabled={paramsId}
                    />
                    {touched.code && errors.code && (
                        <FormHelperText error id="code-error">
                            {errors.code}
                        </FormHelperText>
                    )}
                </Grid>

                {/* Setup Fee Discount Section */}
                <Grid item xs={12}>
                    <Divider />
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5 }}>
                        Setup Fee Discount (optional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        At least one discount section is required.
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <InputLabel>Setup discount amount</InputLabel>
                    <TextField
                        type="number"
                        fullWidth
                        placeholder={values.setupDiscountMethod === 'Amount' ? 'Amount' : 'Percentage'}
                        className="FormInputLabel salutationSelectInput hideSpinButton"
                        value={values.setupDiscountAmount}
                        name="setupDiscountAmount"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        disabled={paramsId}
                        InputProps={{
                            startAdornment: (
                                <PromoSelect
                                    value={values.setupDiscountMethod || ''}
                                    setValue={(newValue) => {
                                        setValues({
                                            ...values,
                                            setupDiscountMethod: newValue,
                                            setupDiscountAmount: newValue ? values.setupDiscountAmount : ''
                                        });
                                    }}
                                />
                            )
                        }}
                    />
                    {touched.setupDiscountAmount && errors.setupDiscountAmount && (
                        <FormHelperText error id="setupDiscountAmount-error">
                            {errors.setupDiscountAmount}
                        </FormHelperText>
                    )}
                    {errors.setupDiscountMethod && typeof errors.setupDiscountMethod === 'string' && (
                        <FormHelperText error id="setupDiscountMethod-error">
                            {errors.setupDiscountMethod}
                        </FormHelperText>
                    )}
                </Grid>

                {/* Subscription Discount Section */}
                <Grid item xs={12}>
                    <Divider />
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                        Subscription Discount (optional)
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <InputLabel>Subscription discount amount</InputLabel>
                    <TextField
                        type="number"
                        fullWidth
                        placeholder={values.subscriptionDiscountMethod === 'Amount' ? 'Amount' : 'Percentage'}
                        className="FormInputLabel salutationSelectInput hideSpinButton"
                        value={values.subscriptionDiscountAmount}
                        name="subscriptionDiscountAmount"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        disabled={paramsId}
                        InputProps={{
                            startAdornment: (
                                <PromoSelect
                                    value={values.subscriptionDiscountMethod || ''}
                                    setValue={(newValue) => {
                                        setValues({
                                            ...values,
                                            subscriptionDiscountMethod: newValue,
                                            subscriptionDiscountAmount: newValue ? values.subscriptionDiscountAmount : '',
                                            subscriptionDiscountDuration: newValue ? values.subscriptionDiscountDuration : ''
                                        });
                                    }}
                                />
                            )
                        }}
                    />
                    {touched.subscriptionDiscountAmount && errors.subscriptionDiscountAmount && (
                        <FormHelperText error id="subscriptionDiscountAmount-error">
                            {errors.subscriptionDiscountAmount}
                        </FormHelperText>
                    )}
                </Grid>

                <Grid item xs={12} sm={4}>
                    <InputLabel>Subscription discount duration</InputLabel>
                    <StaticSelect
                        options={optionPromoDuration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Select"
                        value={values.subscriptionDiscountDuration || ''}
                        name="subscriptionDiscountDuration"
                        setFieldValue={setFieldValue}
                        disabled={paramsId || !values.subscriptionDiscountMethod}
                    />
                    {touched.subscriptionDiscountDuration && errors.subscriptionDiscountDuration && (
                        <FormHelperText error id="subscriptionDiscountDuration-error">
                            {errors.subscriptionDiscountDuration}
                        </FormHelperText>
                    )}
                </Grid>

                {/* Promo Ends Section */}
                <Grid item xs={12}>
                    <Divider />
                    <Stack className="promoEndSection" sx={{ mt: 2 }}>
                        <FormControl>
                            <FormLabel id="branded-app-promo-code-end-radio">
                                <Typography color="#616161" fontWeight="500">
                                    Promo ends
                                </Typography>
                            </FormLabel>
                            <RadioGroup
                                aria-labelledby="branded-app-promo-code-end-radio"
                                defaultValue="never"
                                name="radio-buttons-group"
                                value={values.endType}
                                onChange={(e) => {
                                    setFieldValue('endType', e.currentTarget.value);
                                    setFieldValue('endDate', null);
                                    setFieldValue('endAfterOccurrence', null);
                                }}
                            >
                                <FormControlLabel disabled={paramsId} value="never" control={<Radio color="secondary" />} label="Never" />
                                <Stack>
                                    <Stack direction="row" alignItems="center">
                                        <FormControlLabel
                                            disabled={paramsId}
                                            value="date"
                                            control={<Radio color="secondary" />}
                                            label="On"
                                        />
                                        <LocalizationProvider name="dueDate" dateAdapter={AdapterDayjs}>
                                            <MobileDatePicker
                                                inputFormat="MM/DD/YYYY"
                                                minDate={new Date()}
                                                value={values.endDate}
                                                disabled={values.endType !== 'date' || paramsId}
                                                onChange={(e) => {
                                                    setFieldValue('endDate', e);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            placeholder: 'MM/DD/YYYY'
                                                        }}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </Stack>
                                    {touched.endDate && errors.endDate && (
                                        <FormHelperText sx={{ marginLeft: 0 }} error id="endDate-error">
                                            {errors.endDate}
                                        </FormHelperText>
                                    )}
                                </Stack>
                                <Stack>
                                    <Stack direction="row" alignItems="center">
                                        <FormControlLabel
                                            disabled={paramsId}
                                            value="occurrence"
                                            control={<Radio color="secondary" />}
                                            label="After"
                                        />
                                        <TextField
                                            fullWidth
                                            className="hideSpinButton"
                                            type="number"
                                            value={values.endAfterOccurrence || ''}
                                            placeholder="12 occurrences"
                                            inputProps={{ min: 0 }}
                                            name="After"
                                            onBlur={handleBlur}
                                            disabled={values.endType !== 'occurrence' || paramsId}
                                            onChange={(e) => {
                                                setFieldValue('endAfterOccurrence', parseInt(e.target.value));
                                            }}
                                        />
                                    </Stack>
                                    {touched.endAfterOccurrence && errors.endAfterOccurrence && (
                                        <FormHelperText sx={{ marginLeft: 0 }} error id="endAfterOccurrence-error">
                                            {errors.endAfterOccurrence}
                                        </FormHelperText>
                                    )}
                                    {touched.endType && errors.endType && (
                                        <FormHelperText sx={{ marginLeft: 0 }} error id="endType-error">
                                            {errors.endType}
                                        </FormHelperText>
                                    )}
                                </Stack>
                            </RadioGroup>
                        </FormControl>
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <Divider />
                </Grid>

                <Grid sx={{ pl: '20px' }} item>
                    <Button disabled={isSubmitting} type="submit" variant="contained" color="primary">
                        {paramsId ? 'Update' : 'Create'}
                    </Button>
                </Grid>
            </Grid>
        </MainCard>
    );
};
